import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { ArrowRightOutlined, EnvironmentOutlined, TeamOutlined } from '@ant-design/icons';
import { ROUTE_LEAGUES } from '../../shared/variables/routes';
import { storageImage } from '../../shared/utils/storage-image';

export type HomeLeague = {
  id: string;
  name: string;
  city: string;
  logo: string | null;
  members: number;
  url: string;
  ongoing: boolean;
};

// City pages that already have indexable content; kept as internal links for local SEO.
export const HOME_CITIES: [string, string][] = [
  ['istanbul', 'İstanbul'],
  ['ankara', 'Ankara'],
  ['izmir', 'İzmir'],
  ['antalya', 'Antalya'],
  ['bursa', 'Bursa'],
  ['adana', 'Adana'],
  ['gaziantep', 'Gaziantep'],
  ['konya', 'Konya'],
  ['kayseri', 'Kayseri'],
  ['mersin', 'Mersin'],
  ['eskisehir', 'Eskişehir'],
  ['trabzon', 'Trabzon'],
];

// "Find your league" — the product's promise right under the hero (docs/UI-UX-SYSTEM.md §16.3-A).
export default function HomeLeagues({ leagues }: { leagues: HomeLeague[] }) {
  const { t } = useTranslation('home');
  // Several stored logo URLs are dead; fall back to the league initial instead of a broken image.
  const [failedLogos, setFailedLogos] = useState<Record<string, boolean>>({});
  const listRef = useRef<HTMLUListElement>(null);
  // An SSR image can fail before hydration, when React's onError is not attached yet.
  useEffect(() => {
    const failed: Record<string, boolean> = {};
    listRef.current?.querySelectorAll<HTMLImageElement>('img[data-league-id]').forEach(img => {
      if (img.complete && img.naturalWidth === 0) failed[img.dataset.leagueId as string] = true;
    });
    if (Object.keys(failed).length) setFailedLogos(prev => ({ ...prev, ...failed }));
  }, []);

  return (
    <section className="home-sport__leagues" aria-labelledby="home-leagues-title">
      <div className="container">
        <div className="home-sport__section-head home-sport__section-head--with-link">
          <div>
            <span>{t('leagues.eyebrow')}</span>
            <h2 id="home-leagues-title">{t('leagues.title')}</h2>
          </div>
          <Link href={ROUTE_LEAGUES}>
            <a className="home-sport__section-link">
              {t('leagues.seeAll')} <ArrowRightOutlined aria-hidden="true" />
            </a>
          </Link>
        </div>

        {leagues.length > 0 && (
          <ul className={`home-leagues home-leagues--${leagues.length}`} ref={listRef}>
            {leagues.map(league => (
              <li key={league.id}>
                <Link href={league.url}>
                  <a className="home-league-card">
                    <span className="home-league-card__logo" aria-hidden="true">
                      {league.logo && !failedLogos[league.id] ? (
                        <img
                          src={storageImage(league.logo, 56)}
                          data-league-id={league.id}
                          alt=""
                          width={56}
                          height={56}
                          loading="lazy"
                          decoding="async"
                          onError={() => setFailedLogos(prev => ({ ...prev, [league.id]: true }))}
                        />
                      ) : (
                        <span>{league.name.charAt(0).toLocaleUpperCase('tr-TR')}</span>
                      )}
                    </span>
                    <span className="home-league-card__body">
                      <strong>{league.name}</strong>
                      <span className="home-league-card__meta">
                        {league.city && (
                          <span>
                            <EnvironmentOutlined aria-hidden="true" /> {league.city}
                          </span>
                        )}
                        {league.members > 0 && (
                          <span>
                            <TeamOutlined aria-hidden="true" />{' '}
                            {t('leagues.members', { count: league.members })}
                          </span>
                        )}
                      </span>
                    </span>
                    {league.ongoing && (
                      <span className="home-league-card__live">{t('leagues.ongoing')}</span>
                    )}
                    <ArrowRightOutlined className="home-league-card__arrow" aria-hidden="true" />
                  </a>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <nav className="home-cities" aria-label={t('leagues.citiesLabel')}>
          <span className="home-cities__label">{t('leagues.citiesLabel')}</span>
          <ul>
            {HOME_CITIES.map(([slug, name]) => (
              <li key={slug}>
                <Link href={`/sehir/${slug}`}>
                  <a>{t('leagues.cityLink', { city: name })}</a>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}
